import { toast } from "sonner";
import { createUpdatePayload } from "./unified-field-renderer";
import type {
  BaseEntity,
  SimpleFieldConfig,
  UniversalInlineEditHandler,
  CellCancelHandler,
  FieldValue,
} from "../types/index";

/**
 * Module-level map to track button clicks for blur-vs-button-click logic
 * Replaces globalThis.__cellButtonClickMap to avoid global namespace pollution
 */
const cellButtonClickMap = new Map<string, boolean>();

/**
 * Get whether a button is being clicked for a specific cell
 */
export const getIsClickingButton = (cellKey: string): boolean => {
  return cellButtonClickMap.get(cellKey) || false;
};

/**
 * Set button click state for a specific cell
 */
export const setIsClickingButton = (cellKey: string, value: boolean): void => {
  if (value) {
    cellButtonClickMap.set(cellKey, true);
  } else {
    cellButtonClickMap.delete(cellKey);
  }
};

/**
 * Parameters for createAutoSaveHandler
 */
export interface AutoSaveHandlerParams<T extends BaseEntity = BaseEntity> {
  rowId: string;
  fieldId: string;
  data: T[];
  fieldConfig: SimpleFieldConfig;
  onInlineEdit: UniversalInlineEditHandler;
  onCancel: CellCancelHandler;
  setEditValue?: (value: string) => void;
}

/**
 * Creates an auto-save handler that:
 * 1. Finds the row data by rowId
 * 2. Creates an update payload
 * 3. Calls onInlineEdit with the payload
 * 4. Calls onCancel on success
 * 5. Shows toast errors on failure
 *
 * This centralizes the repeated pattern used across all auto-save edit branches
 */
export const createAutoSaveHandler = <T extends BaseEntity = BaseEntity>(
  params: AutoSaveHandlerParams<T>
) => {
  const { rowId, fieldId, data, fieldConfig, onInlineEdit, onCancel, setEditValue } = params;

  return async (newValue: FieldValue) => {
    try {
      // Update the edit value if setter provided
      if (setEditValue) {
        setEditValue(String(newValue));
      }

      // Find the row data
      const rowData = data.find((item) => item.id === rowId);
      if (!rowData) {
        toast.error("Entry not found");
        return;
      }

      // Create the update payload
      const updatePayload = createUpdatePayload(rowData, fieldId, newValue, fieldConfig);

      // Call the inline edit handler
      await onInlineEdit(updatePayload);

      // Cancel editing on success
      onCancel();
    } catch (error) {
      console.error(`Error updating ${fieldId}:`, error);
      toast.error(`Failed to update ${fieldConfig.label || fieldId}`);
    }
  };
};
